import Button from '@components/Button/Button';
import useIsomorphicLayoutEffect from '@hooks/use-isomorphic-layout-effect';
import useNoRenderRef from '@hooks/use-no-render-ref';
import { logger } from '@modules/logger';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { Crop, PixelCrop } from 'react-image-crop';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

import styles from './ImageEditor.module.scss';

interface ImageEditorProps {
  image: string;
  onLoad?: () => void;
  onEdit?: (editedImage: string, crop: Crop) => void;
  onFinishEdit?: (editedImage: string) => void;
  cropShape?: 'rectangle' | 'circle';
  aspectRatio?: number;
  crop?: Crop;
  disabled?: boolean;
}

interface ImageEditorContextValues {
  finaliseImageEdit: () => void;
}
const ImageEditorContext = createContext<ImageEditorContextValues>({
  finaliseImageEdit: () => logger.warn('finaliseImageEdit was not provided for ImageEditorContext'),
});

const ImageEditor: React.FC<React.PropsWithChildren<ImageEditorProps>> = ({
  image,
  onLoad,
  onEdit,
  onFinishEdit,
  cropShape = 'rectangle',
  aspectRatio,
  crop: cropOverride,
  disabled = false,
}) => {
  // In v11, aspect is no longer part of the Crop object - it's a separate prop
  const [crop, setCrop] = useState<Crop>({
    unit: '%',
    height: 50,
    width: 50,
    x: 25,
    y: 25,
  });
  const hasInitialisedRef = useRef(false);
  const hasInitialisedCropRef = useRef(false);
  const onEditRef = useNoRenderRef(onEdit);
  const [imgNode, setImgNode] = useState<HTMLImageElement>();
  const imgRef = useRef<HTMLImageElement>(null);

  // This is needed due to a bug in Safari. If the image is resized due to having to fit within a
  // height constraint then the width of the parent still takes up all the space available. In other
  // browsers the parent div actually shrinks to fit the child. This is important because it's how
  // react-image-crop determines where the cropper can be moved to. Without the parent div shrinking to fit
  // the crop can essentially be out of bounds. This just makes sure the parent div is always the same width
  // as the image.
  useIsomorphicLayoutEffect(() => {
    if (!imgNode) return;

    const width = imgNode.clientWidth;
    if (imgNode.parentElement) {
      imgNode.parentElement.style.width = `${width}px`;
    }
  }, [imgNode]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: historic
  useEffect(() => {
    hasInitialisedCropRef.current = false;
    hasInitialisedRef.current = false;
  }, [image]);

  // In v11, onImageLoaded is replaced by using onLoad on the img element
  const handleImageLoaded = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const img = e.currentTarget;

      if (cropOverride) {
        setCrop(cropOverride);
      } else {
        const initialCrop = calculateInitialCrop(img, aspectRatio);
        setCrop(initialCrop);
      }

      hasInitialisedCropRef.current = true;
      setImgNode(img);

      onLoad?.();
    },
    [aspectRatio, cropOverride, onLoad],
  );

  useEffect(() => {
    if (!hasInitialisedCropRef.current || hasInitialisedRef.current || !onEditRef.current) return;

    async function handleInitialCrop() {
      if (!imgNode || !onEditRef.current) return;

      hasInitialisedRef.current = true;

      try {
        //TODO: Fix toBlob sometimes returning a blank image
        // We needed this setTimeout, otherwise canvas.toBlob on iOS on Safari will occasionally return a blank image.
        // We have no real idea why this is the case. The following link is the closest thing we could find that somewhat
        // addressed this problem: https://stackoverflow.com/questions/69672178/when-converting-an-svg-to-png-in-the-browser-using-canvas-api-embedded-image-in
        setTimeout(async () => {
          const imageBlob = await getCroppedImg(imgNode, crop);
          const imageReference = URL.createObjectURL(imageBlob);

          onEditRef.current?.(imageReference, crop);
        }, 50);
      } catch (err) {
        return;
      }
    }

    handleInitialCrop();
  }, [crop, onEditRef, imgNode]);

  async function finaliseImageEdit() {
    if (!imgNode || !onFinishEdit) return;

    try {
      const imageBlob = await getCroppedImg(imgNode, crop);
      const imageReference = URL.createObjectURL(imageBlob);
      onFinishEdit(imageReference);
    } catch (err) {
      return;
    }
  }

  // In v11, onComplete receives both pixel and percent crops
  async function handleCompleteCrop(crop: Crop) {
    if (!imgNode || !onEdit) return;

    try {
      const imageBlob = await getCroppedImg(imgNode, crop);
      const imageReference = URL.createObjectURL(imageBlob);
      onEdit(imageReference, crop);
    } catch (err) {
      return;
    }
  }

  return (
    <ImageEditorContext.Provider value={{ finaliseImageEdit }}>
      <ReactCrop
        disabled={disabled}
        crop={crop}
        className={styles['image-crop']}
        onChange={(newCrop) => {
          const normalizedCrop = normalizeCrop(newCrop);
          setCrop(normalizedCrop);
        }}
        onComplete={handleCompleteCrop}
        circularCrop={cropShape === 'circle'}
        aspect={aspectRatio}
      >
        <img
          ref={imgRef}
          src={image}
          alt="Crop preview"
          onLoad={handleImageLoaded}
          style={{ maxWidth: '100%' }}
        />
      </ReactCrop>
    </ImageEditorContext.Provider>
  );
};

/**
 * Normalizes the crop object and fixes any coordinates that don't make sense. This is currently needed
 * because a bug in react-image-crop can cause the crop vertical position to be extremly negative if you
 * create a crop but keep dragging to the top right as you create it.
 * @param crop The crop object
 */
function normalizeCrop(crop: Crop) {
  return {
    ...crop,
    x: crop.x > 0 ? crop.x : 0,
    y: crop.y > 0 ? crop.y : 0,
  };
}

function calculateInitialCrop(img: HTMLImageElement, aspectRatio: number | undefined): Crop {
  const naturalAspectRatio = img.width / img.height;
  const aspect = aspectRatio || naturalAspectRatio;
  const width = img.width / aspect < img.height * aspect ? img.width : img.height * aspect;
  const height = img.width / aspect > img.height * aspect ? img.height : img.width / aspect;
  const y = (img.height - height) / 2;
  const x = (img.width - width) / 2;

  return {
    unit: 'px',
    width,
    height,
    x,
    y,
  };
}

interface ImageEditorButtonProps {
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  type?: 'default' | 'primary';
  action?: 'save';
  disabled?: boolean;
}

export const ImageEditorButton: React.FC<React.PropsWithChildren<ImageEditorButtonProps>> = ({
  children,
  onClick,
  type = 'default',
  action,
  disabled = false,
}) => {
  const { finaliseImageEdit } = useContext(ImageEditorContext);

  function handleClick(event: React.MouseEvent<HTMLButtonElement>) {
    if (action) finaliseImageEdit();
    onClick(event);
  }
  return (
    <Button
      onClick={handleClick}
      variant="text"
      size="tiny"
      type={type}
      disabled={disabled}
      className={styles['image-editor-button']}
    >
      {children}
    </Button>
  );
};

export const ImageEditorButtons: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  return <div className={styles['image-editor-buttons']}>{children}</div>;
};

export const ImageEditorFrame: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  return <div className={styles['image-editor-frame']}>{children}</div>;
};

/**
 * @param {HTMLImageElement} image - Image File Object
 * @param {Object} crop - crop Object
 * @param {String} fileName - Name of the returned file in Promise
 */
function getCroppedImg(image: HTMLImageElement, crop: Crop): Promise<Blob> {
  if (crop.x == null || crop.y == null || crop.width == null || crop.height == null)
    throw new Error('Crop measurements missing');

  const canvas = document.createElement('canvas');
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;

  canvas.width = crop.width * scaleX;
  canvas.height = crop.height * scaleY;
  const ctx = canvas.getContext('2d');

  if (!ctx) throw new Error('Could not get a 2d context');
  ctx.clearRect(0, 0, crop.width * scaleX, crop.height * scaleY);
  ctx.drawImage(
    image,
    crop.x * scaleX,
    crop.y * scaleY,
    crop.width * scaleX,
    crop.height * scaleY,
    0,
    0,
    crop.width * scaleX,
    crop.height * scaleY,
  );

  const isEmpty = !new Uint32Array(ctx.getImageData(0, 0, canvas.width, canvas.height).data.buffer).some(
    (x) => x !== 0,
  );

  // As a blob
  return new Promise((resolve, reject) => {
    if (isEmpty) reject();
    else {
      canvas.toBlob(
        (blob) => {
          if (!blob) reject();
          else {
            resolve(blob);
          }
        },
        'image/png',
        1,
      );
    }
  });
}

export default ImageEditor;
